import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, Square, Trash2, Terminal, Cpu, MemoryStick, HardDrive, KeyRound } from 'lucide-react'
import { useState } from 'react'
import Card, { CardHeader } from '../components/Card'
import Button from '../components/Button'
import StatusBadge from '../components/StatusBadge'
import VMConsole from '../components/VMConsole'
import { getVM, startVM, stopVM, destroyVM } from '../lib/api'

export default function VMDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: vm, isLoading } = useQuery({
    queryKey: ['vm', id],
    queryFn: () => getVM(id!),
    enabled: !!id,
    refetchInterval: 5000,
  })

  const [startError, setStartError] = useState<string | null>(null)
  const [consoleOpen, setConsoleOpen] = useState(false)

  const startMutation = useMutation({
    mutationFn: () => startVM(id!),
    onSuccess: () => {
      setStartError(null)
      queryClient.invalidateQueries({ queryKey: ['vm', id] })
    },
    onError: (err: unknown) => {
      // Surface the real reason a boot failed (e.g. no KVM engine on this host)
      // instead of silently leaving the VM stopped.
      setStartError(err instanceof Error ? err.message : 'Failed to start VM')
    },
  })

  const stopMutation = useMutation({
    mutationFn: () => stopVM(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vm', id] }),
  })

  const destroyMutation = useMutation({
    mutationFn: () => destroyVM(id!),
    onSuccess: () => navigate('/vms'),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-armored-600" />
      </div>
    )
  }

  if (!vm) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">VM not found</h3>
        <p className="mt-2 text-gray-500">The VM may have been removed</p>
        <Button onClick={() => navigate('/vms')} className="mt-4">
          Back to VMs
        </Button>
      </div>
    )
  }

  const handleDestroy = () => {
    if (confirm(`Are you sure you want to destroy VM ${id}?`)) {
      destroyMutation.mutate()
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/vms')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{vm.id}</h1>
            <div className="flex items-center space-x-2 mt-1">
              <StatusBadge status={vm.state} />
              <span className="text-sm text-gray-500">{vm.kernel_profile}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {vm.state !== 'running' ? (
            <Button
              onClick={() => startMutation.mutate()}
              loading={startMutation.isPending}
              icon={<Play className="h-4 w-4" />}
            >
              Start
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => stopMutation.mutate()}
              loading={stopMutation.isPending}
              icon={<Square className="h-4 w-4" />}
            >
              Stop
            </Button>
          )}
          {/* Live serial console: opens a WebSocket terminal to the guest's
              /dev/ttyS0 (the L1 ssh-bridge relays volt-vmm's console socket).
              Only meaningful while the VM is running. */}
          <Button
            variant="secondary"
            disabled={vm.state !== 'running'}
            title={vm.state !== 'running' ? 'Start the VM to open a console' : 'Open a live shell'}
            onClick={() => setConsoleOpen((v) => !v)}
            icon={<Terminal className="h-4 w-4" />}
          >
            {consoleOpen ? 'Hide Console' : 'Console'}
          </Button>
          <Button
            variant="danger"
            onClick={handleDestroy}
            loading={destroyMutation.isPending}
            icon={<Trash2 className="h-4 w-4" />}
          >
            Destroy
          </Button>
        </div>
      </div>

      {startError && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Could not start this VM: {startError}
        </div>
      )}

      {/* Access card — how a user actually reaches the machine. When running, the
          user can open a live in-browser serial console (a real root shell). */}
      <Card className="mb-6">
        <CardHeader
          title="Access"
          description="Open a live shell to this VM in your browser"
        />
        {vm.state === 'running' ? (
          consoleOpen ? (
            <VMConsole id={vm.id} />
          ) : (
            <div className="flex items-start space-x-3">
              <KeyRound className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700">
                  This VM is running. Click <span className="font-medium">Console</span> above to
                  open a live root shell in your browser.
                </p>
                {vm.ip && (
                  <dd className="mt-2 font-mono text-sm bg-gray-50 rounded px-3 py-2 border border-gray-200">
                    ssh root@{vm.ip}
                  </dd>
                )}
              </div>
            </div>
          )
        ) : (
          <p className="text-sm text-gray-500">
            Start this VM to open a console / shell.
          </p>
        )}
      </Card>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <Card>
          <CardHeader title="Configuration" />
          <dl className="space-y-4">
            <div className="flex items-start space-x-3">
              <Cpu className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm text-gray-500">Kernel Profile</dt>
                <dd className="font-medium">{vm.kernel_profile}</dd>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <MemoryStick className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm text-gray-500">Memory</dt>
                <dd className="font-medium">{vm.memory}</dd>
              </div>
            </div>
            {vm.cpu_shares && (
              <div className="flex items-start space-x-3">
                <Cpu className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <dt className="text-sm text-gray-500">CPU Shares</dt>
                  <dd className="font-medium">{vm.cpu_shares}</dd>
                </div>
              </div>
            )}
            <div className="flex items-start space-x-3">
              <HardDrive className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <dt className="text-sm text-gray-500">Root Filesystem</dt>
                <dd className="font-mono text-xs break-all">{vm.rootfs}</dd>
              </div>
            </div>
          </dl>
        </Card>

        {/* Status */}
        <Card>
          <CardHeader title="Status" />
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-gray-500">State</dt>
              <dd className="mt-1"><StatusBadge status={vm.state} /></dd>
            </div>
            {vm.pid && (
              <div>
                <dt className="text-sm text-gray-500">Process ID</dt>
                <dd className="font-mono">{vm.pid}</dd>
              </div>
            )}
            {vm.ip && (
              <div>
                <dt className="text-sm text-gray-500">IP Address</dt>
                <dd className="font-mono">{vm.ip}</dd>
              </div>
            )}
            {vm.systemd_unit && (
              <div>
                <dt className="text-sm text-gray-500">SystemD Unit</dt>
                <dd className="font-mono text-xs">{vm.systemd_unit}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Labels */}
        <Card>
          <CardHeader title="Labels" />
          {vm.labels && Object.keys(vm.labels).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(vm.labels).map(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">{key}</span>
                  <span className="text-gray-500">=</span>
                  <span className="text-sm">{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No labels</p>
          )}
        </Card>
      </div>
    </div>
  )
}
